import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { format } from "date-fns";
import { TbSitemap } from "react-icons/tb";

// Icons
import {
    ClipboardList,
    AlertOctagon,
    Wrench,
    Filter,
    ChevronRight,
    ArrowUpRight,
    X,
    AlertTriangle,
    ChevronLeft,
    ChevronDown,
    WalletMinimal,
    Dice1,
} from 'lucide-react';

// Charts
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    Cell
} from 'recharts';

// --- IMPORT YOUR EXISTING COMPONENTS HERE ---
import GoaHeatmap from "../../components/activityComponent/GoaHeatmap";

// --- MOCK DATA FOR HEATMAP (Hospitality Context) ---
const MOCK_HEATMAP_DATA = [
    {
        zone_name: "Coastal Club Belt (North)",
        booking_count: 120, // High density of clubs
        details: [
            { booking_id: "CLUB-001", coordinate: [15.5494, 73.7535] }, // Baga/Calangute
            { booking_id: "REST-002", coordinate: [15.5550, 73.7510] },
            { booking_id: "HOTEL-003", coordinate: [15.6061, 73.7358] }, // Vagator/Anjuna
            { booking_id: "CLUB-004", coordinate: [15.5494, 73.7535] }, // Duplicate for heat intensity
            { booking_id: "SHACK-005", coordinate: [15.5400, 73.7600] },
        ],
        gps_coordinates: ["15.5494, 73.7535", "15.6061, 73.7358"]
    },
    {
        zone_name: "Luxury Resort Zone (South)",
        booking_count: 45,
        details: [
            { booking_id: "RESORT-006", coordinate: [15.2832, 73.9862] }, // Varca/Benaulim
            { booking_id: "HOTEL-007", coordinate: [15.1764, 73.9536] }, // Palolem
        ]
    },
    {
        zone_name: "City Restaurants (Panjim)",
        booking_count: 80,
        details: [
            { booking_id: "REST-008", coordinate: [15.4909, 73.8278] }, // Panjim
            { booking_id: "CASINO-009", coordinate: [15.5000, 73.8300] },
        ]
    }
];

// --- MOCK DATA FOR DASHBOARD (Inspection Teams) ---
const workloadData = [
    { team: 'Food Safety', load: 85, capacity: 100 }, // Restaurants/Kitchens
    { team: 'Noise Ctrl', load: 95, capacity: 100 }, // Clubs
    { team: 'Excise', load: 60, capacity: 100 },      // Liquor Licenses
    { team: 'Fire Safety', load: 45, capacity: 100 }, // Hotels
];

// --- EQUIPMENT DATA (Hospitality Inspection Gear) ---
const equipmentData = [
    { id: 1, name: 'Decibel Meter Pro-X', lastService: '2025-12-15', nextDue: '2026-02-28', status: 'Critical', type: 'Noise Monitoring' },
    { id: 2, name: 'Digital Food Thermometer', lastService: '2026-01-10', nextDue: '2026-04-10', status: 'Good', type: 'Hygiene Check' },
    { id: 3, name: 'Water Quality Tester', lastService: '2025-11-20', nextDue: '2026-03-05', status: 'Maintenance', type: 'Pool Safety' },
    { id: 4, name: 'Bar License Scanner', lastService: '2026-01-20', nextDue: '2026-05-20', status: 'Good', type: 'Enforcement' },
    { id: 5, name: 'Kitchen Fire Suppression Kit', lastService: '2025-06-15', nextDue: '2026-01-15', status: 'Critical', type: 'Fire Safety' },
];

// --- HELPER COMPONENTS ---
const Card = ({ children, className = "", isDark }) => (
    <div className={`${isDark ? 'bg-[#1e293b] border-slate-700' : 'bg-white border-slate-200'} border rounded-xl shadow-sm ${className}`}>
        {children}
    </div>
);

const Badge = ({ status, isDark }) => {
    const styles = {
        Critical: isDark ? "bg-red-900/30 text-red-400 border-red-800" : "bg-red-100 text-red-700 border-red-200",
        Maintenance: isDark ? "bg-amber-900/30 text-amber-400 border-amber-800" : "bg-amber-100 text-amber-700 border-amber-200",
        Good: isDark ? "bg-emerald-900/30 text-emerald-400 border-emerald-800" : "bg-emerald-100 text-emerald-700 border-emerald-200",
        Default: isDark ? "bg-slate-800 text-slate-400 border-slate-700" : "bg-slate-100 text-slate-700 border-slate-200"
    };
    return (
        <span className={`px-2.5 py-1 rounded-md text-xs font-bold border ${styles[status] || styles.Default}`}>
            {status}
        </span>
    );
};

const DepartmentDashboardContent = () => {
    const themeMode = useSelector((state) => state.theme.mode);
    const [showForm, setShowForm] = useState(false);
    const [selectedZone, setSelectedZone] = useState(null);

    // --- HEATMAP STATE & LOGIC ---
    const today = new Date();
    const formattedToday = format(today, "yyyy-MM-dd");

    const [selectDate, setSelectDate] = useState({
        startDate: formattedToday,
        endDate: formattedToday,
    });

    const [datasets, setDatasets] = useState({});
    const [heatmapData, setHeatmapData] = useState([]);

    // Initialize Mock Data on mount
    useEffect(() => {
        setHeatmapData(MOCK_HEATMAP_DATA);
    }, []);

    // --- HEATMAP PROCESSING LOGIC ---
    useEffect(() => {
        if (!heatmapData || heatmapData.length === 0) {
            setDatasets({});
            return;
        }

        const allPoints = [];
        let pointCounter = 1;

        heatmapData.forEach((zone) => {
            const zoneName = zone.zone_name;
            const totalZoneBookings = zone.booking_count || 0;

            if (zone.details && Array.isArray(zone.details)) {
                zone.details.forEach((detail) => {
                    const bookingId = detail.booking_id;
                    if (detail.coordinate && detail.coordinate.length >= 2) {
                        const lat = parseFloat(detail.coordinate[0]);
                        const lng = parseFloat(detail.coordinate[1]);
                        if (isNaN(lat) || isNaN(lng)) return;

                        const existingIndex = allPoints.findIndex(p =>
                            p.lat.toFixed(6) === lat.toFixed(6) &&
                            p.lng.toFixed(6) === lng.toFixed(6) &&
                            p.zone === zoneName
                        );

                        if (existingIndex !== -1) {
                            allPoints[existingIndex].booking_ids.push(bookingId);
                            allPoints[existingIndex].weight += 1;
                        } else {
                            const newPoint = {
                                id: `point-${pointCounter++}`,
                                lat,
                                lng,
                                zone: zoneName,
                                weight: 1,
                                booking_ids: [bookingId],
                                original_zone: zoneName,
                                coordinate_source: "details",
                                total_zone_bookings: totalZoneBookings,
                            };
                            allPoints.push(newPoint);
                        }
                    }
                });
            }

            if (zone.gps_coordinates && Array.isArray(zone.gps_coordinates)) {
                zone.gps_coordinates.forEach((coordStr) => {
                    if (!coordStr) return;
                    const cleaned = coordStr.trim().replace(/\s+/g, " ");
                    const parts = cleaned.split(/[, ]+/);
                    if (parts.length < 2) return;
                    const lat = parseFloat(parts[0]);
                    const lng = parseFloat(parts[1]);
                    if (isNaN(lat) || isNaN(lng)) return;

                    const matchingDetail = allPoints.find(p =>
                        p.zone === zoneName &&
                        p.lat.toFixed(6) === lat.toFixed(6) &&
                        p.lng.toFixed(6) === lng.toFixed(6)
                    );

                    if (matchingDetail) {
                        matchingDetail.weight += 1;
                        matchingDetail.coordinate_source = "both";
                    } else {
                        const newPoint = {
                            id: `point-${pointCounter++}`,
                            lat,
                            lng,
                            zone: zoneName,
                            weight: 1,
                            booking_ids: [],
                            original_zone: zoneName,
                            coordinate_source: "gps",
                            total_zone_bookings: totalZoneBookings,
                        };
                        allPoints.push(newPoint);
                    }
                });
            }
        });

        setDatasets({ [formattedToday]: allPoints });
    }, [heatmapData, formattedToday]);

    // --- REAL-TIME CALENDAR STATE ---
    const [viewDate, setViewDate] = useState(new Date(2026, 1, 24));
    const [selectedCalDate, setSelectedCalDate] = useState(new Date(2026, 1, 24));
    const [showMonthPicker, setShowMonthPicker] = useState(false);
    const [showYearPicker, setShowYearPicker] = useState(false);

    const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

    const daysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
    const firstDay = (y, m) => new Date(y, m, 1).getDay();

    const getZoneDetails = (id) => {
        const zones = {
            4: { title: "Club Zone - Baga", needs: ["Noise Level Audit", "Fire Exit Clearance Check"], risk: "High" },
            7: { title: "Hotel Zone - Panjim", needs: ["Kitchen Hygiene Inspection", "Pool Safety Check"], risk: "Medium" },
            default: { title: `Hospitality Unit ${id}`, needs: ["License Verification", "Staff Health Cards"], risk: "Low" }
        };
        return zones[id] || zones.default;
    };

    // Dynamic Classes
    const textColorClass = themeMode ? "text-slate-100" : "text-primary";
    const subTextColor = themeMode ? "text-slate-400" : "text-slate-500";
    const cardBg = themeMode ? "bg-[#1e293b]" : "bg-white";
    const borderThem = themeMode ? "border-slate-700" : "border-slate-200";
    const scrollBg = themeMode ? "sidebar_scroll_dark" : "sidebar_scroll";
    const tableHeaderBg = themeMode ? "bg-slate-800/50" : "bg-slate-50/50";
    const inputBg = themeMode ? "bg-slate-800 border-slate-700 text-white" : "bg-slate-50 border-slate-200 text-slate-900";

    return (
        <div className={`${themeMode ? 'bg-[#0f172a]' : 'bg-white'} h-full transition-colors duration-300 relative`}>

            {/* --- ZONE DETAIL POPUP --- */}
            {selectedZone && (
                <div className="absolute inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <Card isDark={themeMode} className="w-full max-w-sm shadow-2xl animate-in zoom-in duration-200">
                        <div className={`p-4 border-b ${borderThem} flex justify-between items-center bg-gradient-to-r ${themeMode ? 'from-slate-800 to-slate-900' : 'from-slate-50 to-white'}`}>
                            <h4 className={`font-black text-sm ${textColorClass} uppercase tracking-tight`}>{getZoneDetails(selectedZone).title}</h4>
                            <button onClick={() => setSelectedZone(null)} className="p-1 hover:bg-red-500/10 rounded-full text-red-500 transition-colors"><X size={18} /></button>
                        </div>
                        <div className="p-5 space-y-4">
                            <div className="space-y-2">
                                <p className={`text-[10px] font-black uppercase ${subTextColor}`}>Required Inspection:</p>
                                {getZoneDetails(selectedZone).needs.map((item, idx) => (
                                    <div key={idx} className={`flex items-center gap-2 p-2 rounded-lg text-xs font-bold ${themeMode ? 'bg-slate-800 text-slate-300' : 'bg-slate-50 text-slate-700'}`}>
                                        <AlertTriangle size={14} className="text-amber-500" /> {item}
                                    </div>
                                ))}
                            </div>
                            <button onClick={() => setSelectedZone(null)} className="w-full py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-lg transition-all">Acknowledge & Dispatch Team</button>
                        </div>
                    </Card>
                </div>
            )}

            {/* --- INSPECTION FORM OVERLAY --- */}
            {showForm && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <Card isDark={themeMode} className="w-full max-w-lg shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
                        <div className={`p-6 border-b ${borderThem} flex justify-between items-center`}>
                            <div>
                                <h2 className={`text-xl font-black ${textColorClass}`}>New Establishment Audit</h2>
                                <p className={`text-xs ${subTextColor} font-bold uppercase`}>Enter Venue Details</p>
                            </div>
                            <button onClick={() => setShowForm(false)} className={`${subTextColor} hover:text-red-500 transition-colors`}><X size={24} /></button>
                        </div>
                        <form className="p-6 space-y-4" onSubmit={(e) => { e.preventDefault(); setShowForm(false); }}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className={`text-[10px] font-black uppercase ${subTextColor}`}>Establishment Name</label>
                                    <input type="text" placeholder="e.g. Club Cubana" className={`w-full px-4 py-2.5 rounded-lg border text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all ${inputBg}`} />
                                </div>
                                <div className="space-y-1.5">
                                    <label className={`text-[10px] font-black uppercase ${subTextColor}`}>Inspection Type</label>
                                    <select className={`w-full px-4 py-2.5 rounded-lg border text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all ${inputBg}`}>
                                        <option>Food Safety & Hygiene</option>
                                        <option>Noise Control</option>
                                        <option>Fire Safety</option>
                                        <option>Liquor License</option>
                                    </select>
                                </div>
                            </div>
                            <div className="flex gap-3 pt-2">
                                <button type="button" onClick={() => setShowForm(false)} className={`flex-1 py-3 rounded-xl font-bold text-sm border ${borderThem} ${textColorClass} hover:bg-slate-500/10 transition-colors`}>Cancel</button>
                                <button type="submit" className="flex-1 py-3 rounded-xl font-bold text-sm bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-500/20 transition-all">Schedule Audit</button>
                            </div>
                        </form>
                    </Card>
                </div>
            )}

            {/* Header */}
            <div className={`py-4 md:px-12 px-6 border-b flex justify-between items-center h-[57px] ${borderThem}`}>
                <div className={`flex ps-3 md:ps-0 ${textColorClass} text-sm md:text-base items-center gap-x-2`}>
                    <WalletMinimal size={16} />
                    <p className="font-semibold">Hospitality Enforcement Dashboard</p>
                </div>
                <button onClick={() => setShowForm(true)} className={`${themeMode ? 'bg-blue-600 hover:bg-blue-500' : 'bg-slate-900 hover:bg-slate-800'} text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors`}>
                    + New Audit
                </button>
            </div>

            {/* Main Content Area */}
            <div className={`px-3 md:px-5 lg:px-12 overflow-y-auto h-[calc(91vh-0px)] pt-4 pb-10 ${scrollBg}`}>
                <div className='space-y-6'>

                    {/* ROW 1 (Stats) */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <Card isDark={themeMode} className="p-6 flex flex-col border-l-4 border-l-blue-500 group">
                            <div className="flex-1">
                                <div className="flex justify-between items-center mb-1">
                                    <span className={`${subTextColor} text-[11px] font-bold uppercase tracking-wider -mt-3`}>Venue Audits</span>
                                    <div className={`${themeMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-50 text-blue-600'} p-2 rounded-xl group-hover:scale-110 transition-transform`}><ClipboardList size={20} /></div>
                                </div>
                                <div className="flex items-baseline gap-2">
                                    <span className={`text-4xl font-extrabold ${themeMode ? 'text-white' : 'text-slate-900'}`}>14</span>
                                    <span className={`text-sm ${subTextColor} font-medium`}>scheduled today</span>
                                </div>
                            </div>
                            <button className={`mt-5 w-full py-2.5 ${themeMode ? 'bg-slate-800 text-blue-400 hover:bg-blue-600 hover:text-white' : 'bg-blue-50/50 text-blue-700 hover:bg-blue-600 hover:text-white'} text-[11px] font-black uppercase tracking-widest rounded-lg transition-all flex items-center justify-center gap-1 group/btn`}>
                                View Schedule <ChevronRight size={14} className="group-hover/btn:translate-x-1 transition-transform" />
                            </button>
                        </Card>

                        <Card isDark={themeMode} className={`p-5 flex flex-col ${themeMode ? 'bg-red-900/10 border-red-900/50' : 'bg-red-50/40 border-red-100'} border-l-4 border-l-red-500 h-full group relative overflow-hidden`}>
                            <div className="absolute -right-2 -bottom-2 text-red-500/10"><AlertOctagon size={80} /></div>
                            <div className="relative z-10 flex-1">
                                <span className={`${themeMode ? 'text-red-400' : 'text-red-700'} text-[11px] font-black uppercase flex items-center gap-2 tracking-widest mb-1`}>
                                    <div className="w-2 h-2 bg-red-500 rounded-full animate-ping" /><AlertOctagon size={14} /> Critical Violations
                                </span>
                                <div className="flex items-baseline gap-2">
                                    <span className={`text-4xl font-extrabold ${themeMode ? 'text-red-400' : 'text-red-700'}`}>08</span>
                                    <span className={`text-[11px] ${themeMode ? 'text-red-400/70' : 'text-red-600/70'} font-bold uppercase`}>Action Required</span>
                                </div>
                                <div className="mt-4 space-y-2">
                                    {[{ t: 'Noise Violation (Club X)', d: '-2 hrs' }, { t: 'Expired License (Hotel Y)', d: '-5 hrs' }].map((task, idx) => (
                                        <div key={idx} className={`${themeMode ? 'bg-slate-800/80 border-red-900/30 text-red-300' : 'bg-white/80 border-red-100 text-red-800'} px-3 py-2 rounded-lg border text-[11px] font-bold flex justify-between shadow-sm`}>
                                            <span className="flex items-center gap-2"><div className="w-1 h-1 bg-red-400 rounded-full" /> {task.t}</span>
                                            <span className={`${themeMode ? 'bg-red-900/50' : 'bg-red-100'} px-1.5 py-0.5 rounded text-[9px]`}>{task.d}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </Card>

                        <Card isDark={themeMode} className="p-5 flex flex-col h-full">
                            <span className={`${subTextColor} text-[11px] font-black uppercase tracking-widest mb-4`}>Department Load (Hotels/Clubs)</span>
                            <div className="flex-1 w-full min-h-[85px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={workloadData} layout="vertical" margin={{ left: -30, right: 0 }}>
                                        <XAxis type="number" hide />
                                        <YAxis dataKey="team" type="category" width={65} tick={{ fontSize: 9, fontWeight: 800, fill: themeMode ? '#94a3b8' : '#64748b' }} axisLine={false} tickLine={false} />
                                        <Tooltip cursor={{ fill: themeMode ? '#334155' : '#f8fafc' }} contentStyle={{ backgroundColor: themeMode ? '#1e293b' : '#fff', borderRadius: '12px', border: 'none', color: themeMode ? '#fff' : '#000' }} />
                                        <Bar dataKey="load" radius={[0, 6, 6, 0]} barSize={10}>
                                            {workloadData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.load > 90 ? '#ef4444' : entry.load > 70 ? '#f59e0b' : '#3b82f6'} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </Card>
                    </div>

                    <div className="grid grid-cols-12 gap-6">

                        <Card isDark={themeMode} className="px-6 py-3 md:col-span-8 col-span-12 relative overflow-hidden flex flex-col min-h-[350px]">
                            {/* Header */}
                            <div className="flex justify-between items-start mb-3">
                                <div className={`flex gap-x-2 ${textColorClass}`}>
                                    <TbSitemap className="cursor-pointer" size={20} />
                                    <p className="font-semibold">Hospitality Cluster Heatmap</p>
                                </div>
                                <div className={`flex gap-3 ${themeMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-100'} p-2 rounded-xl border`}>
                                    <div className="text-[10px] font-black text-slate-500 uppercase flex items-center gap-2">
                                        <div className="w-2 h-2 bg-red-500 rounded-full" /> High Activity
                                    </div>
                                </div>
                            </div>

                            {/* Actual Heatmap Render */}
                            <div className="flex-1 w-full h-full min-h-[300px] relative z-20">
                                <GoaHeatmap
                                    datasets={datasets}
                                    defaultTimeKey={formattedToday}
                                    selectDate={selectDate}
                                    setSelectDate={setSelectDate}
                                />
                            </div>
                        </Card>

                        <Card isDark={themeMode} className="px-6 py-3 sm:col-span-4  col-span-12 flex flex-col h-auto max-h-max">
                            {/* Pro Header with Navigation & Pickers */}
                            <div className={`flex gap-x-2 border-b  pb-3 ${textColorClass}`}>
                                <Dice1 className="cursor-pointer" size={20} />
                                <p className="font-semibold">Surprise Audit Calendar</p>
                            </div>
                            <div className="flex justify-between items-center mb-6">
                                <div className="flex items-center gap-2 mt-3">
                                    {/* Month Picker Dropdown */}
                                    <div className="relative">
                                        <button onClick={() => { setShowMonthPicker(!showMonthPicker); setShowYearPicker(false); }} className={`flex items-center gap-1 px-2 py-1 rounded-lg font-black text-sm transition-all ${themeMode ? 'bg-slate-800 text-white hover:bg-slate-700' : 'bg-slate-100 text-slate-800 hover:bg-slate-200'}`}>
                                            {months[viewDate.getMonth()]} <ChevronDown size={14} />
                                        </button>
                                        {showMonthPicker && (
                                            <div className={`absolute top-full left-0 mt-2 w-40 p-2 z-[100] rounded-xl border shadow-2xl grid grid-cols-2 gap-1 ${themeMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                                                {months.map((m, i) => (
                                                    <button key={m} onClick={() => { setViewDate(new Date(viewDate.getFullYear(), i, 1)); setShowMonthPicker(false); }} className={`text-[10px] font-bold p-2 rounded-lg uppercase ${viewDate.getMonth() === i ? 'bg-blue-600 text-white' : themeMode ? 'text-slate-400 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-50'}`}>{m.substring(0, 3)}</button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    {/* Year Picker Dropdown */}
                                    <div className="relative">
                                        <button onClick={() => { setShowYearPicker(!showYearPicker); setShowMonthPicker(false); }} className={`flex items-center gap-1 px-2 py-1 rounded-lg font-black text-sm transition-all ${themeMode ? 'bg-slate-800 text-white hover:bg-slate-700' : 'bg-slate-100 text-slate-800 hover:bg-slate-200'}`}>
                                            {viewDate.getFullYear()} <ChevronDown size={14} />
                                        </button>
                                        {showYearPicker && (
                                            <div className={`absolute top-full left-0 mt-2 w-32 p-2 z-[100] rounded-xl border shadow-2xl max-h-48 overflow-y-auto ${themeMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                                                {years.map(y => (
                                                    <button key={y} onClick={() => { setViewDate(new Date(y, viewDate.getMonth(), 1)); setShowYearPicker(false); }} className={`w-full text-left text-[11px] font-bold p-2.5 rounded-lg ${viewDate.getFullYear() === y ? 'bg-blue-600 text-white' : themeMode ? 'text-slate-400 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-50'}`}>{y}</button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="flex gap-1">
                                    <button onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1))} className={`p-1.5 rounded-lg ${themeMode ? 'hover:bg-slate-800 text-slate-400' : 'hover:bg-slate-100 text-slate-600'}`}><ChevronLeft size={18} /></button>
                                    <button onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1))} className={`p-1.5 rounded-lg ${themeMode ? 'hover:bg-slate-800 text-slate-400' : 'hover:bg-slate-100 text-slate-600'}`}><ChevronRight size={18} /></button>
                                </div>
                            </div>

                            <div className="grid grid-cols-7 mb-4">
                                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(day => <div key={day} className="text-center text-[11px] font-black text-slate-500 uppercase">{day}</div>)}
                            </div>

                            <div className="grid grid-cols-7 gap-1">
                                {Array.from({ length: firstDay(viewDate.getFullYear(), viewDate.getMonth()) }).map((_, i) => <div key={`empty-${i}`} className="aspect-square" />)}
                                {Array.from({ length: daysInMonth(viewDate.getFullYear(), viewDate.getMonth()) }).map((_, i) => {
                                    const day = i + 1;
                                    const isSelected = selectedCalDate.getDate() === day && selectedCalDate.getMonth() === viewDate.getMonth() && selectedCalDate.getFullYear() === viewDate.getFullYear();
                                    const hasAudit = [5, 12, 18, 25].includes(day) && viewDate.getMonth() === 1;

                                    return (
                                        <div key={day} onClick={() => setSelectedCalDate(new Date(viewDate.getFullYear(), viewDate.getMonth(), day))} className="aspect-square flex items-center justify-center relative group cursor-pointer">
                                            {isSelected && <div className={`absolute inset-1.5 ${themeMode ? 'bg-blue-500' : 'bg-slate-900'} rounded-xl shadow-lg`} />}
                                            {!isSelected && hasAudit && <div className={`absolute inset-1.5 ${themeMode ? 'bg-blue-900/40 border-blue-800' : 'bg-blue-50 border-blue-100'} rounded-xl border group-hover:bg-blue-600 transition-all`} />}
                                            <span className={`relative z-10 text-sm font-bold ${isSelected ? 'text-white' : hasAudit ? 'text-blue-500 group-hover:text-white' : themeMode ? 'text-slate-400' : 'text-slate-600'}`}>{day}</span>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className={`mt-auto p-3 ${themeMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'} rounded-xl border border-dashed`}>
                                <div className="flex items-center gap-3">
                                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                                    <span className={`text-[10px] font-black ${themeMode ? 'text-slate-300' : 'text-slate-700'} uppercase`}>
                                        {selectedCalDate.toDateString() === new Date(2026, 1, 28).toDateString() ? "Feb 28: Club Noise Blitz" : `Plan: ${selectedCalDate.toLocaleDateString()}`}
                                    </span>
                                </div>
                            </div>
                        </Card>
                    </div>

                    <Card isDark={themeMode} className="overflow-hidden">
                        <div className={`p-5 border-b ${borderThem} flex flex-wrap gap-4 justify-between items-center ${cardBg}`}>
                            <div className="flex items-center gap-3">
                                <div className={`p-2 ${themeMode ? 'bg-slate-800 text-slate-400' : 'bg-slate-100 text-slate-600'} rounded-lg shadow-inner`}><Wrench size={18} /></div>
                                <div>
                                    <h3 className={`font-black ${themeMode ? 'text-white' : 'text-slate-800'}`}>Inspection Equipment Status</h3>
                                    <p className={`text-[10px] ${subTextColor} font-bold uppercase`}>Audit Tools</p>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <button className={`flex items-center gap-2 px-4 py-2 ${themeMode ? 'bg-slate-800 border-slate-700 text-slate-300' : 'bg-white border-slate-200 text-slate-600'} border rounded-xl text-[11px] font-black uppercase tracking-tight`}><Filter size={14} /> Filter</button>
                            </div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left border-separate border-spacing-0">
                                <thead className={`text-[10px] ${subTextColor} font-black uppercase tracking-wider ${tableHeaderBg}`}>
                                    <tr>
                                        {['Tool Name', 'Inspection Type', 'Last Calibrated', 'Next Due', 'Status', 'Actions'].map((h, idx) => (
                                            <th key={idx} className={`px-6 py-4 border-b ${borderThem} ${idx === 5 ? 'text-right' : ''}`}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className={cardBg}>
                                    {equipmentData.map((item) => (
                                        <tr key={item.id} className={`${themeMode ? 'hover:bg-slate-800/50' : 'hover:bg-blue-50/30'} transition-all group`}>
                                            <td className={`px-6 py-4 border-b ${borderThem}`}>
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-8 h-8 ${themeMode ? 'bg-slate-800 text-slate-400' : 'bg-slate-100 text-slate-500'} rounded-lg flex items-center justify-center font-bold text-xs`}>{item.name.charAt(0)}</div>
                                                    <span className={`font-bold ${themeMode ? 'text-slate-200' : 'text-slate-700'}`}>{item.name}</span>
                                                </div>
                                            </td>
                                            <td className={`px-6 py-4 border-b ${borderThem}`}><span className={subTextColor}>{item.type}</span></td>
                                            <td className={`px-6 py-4 border-b ${borderThem}`}><span className={`${subTextColor} text-xs font-bold`}>{item.lastService}</span></td>
                                            <td className={`px-6 py-4 border-b ${borderThem}`}>
                                                <div className="flex flex-col">
                                                    <span className={`font-black tracking-tighter ${new Date(item.nextDue) < new Date() ? 'text-red-500' : themeMode ? 'text-slate-200' : 'text-slate-700'}`}>{item.nextDue}</span>
                                                    {new Date(item.nextDue) < new Date() && <span className="text-[9px] text-red-400 font-black uppercase">Overdue</span>}
                                                </div>
                                            </td>
                                            <td className={`px-6 py-4 border-b ${borderThem}`}><Badge status={item.status} isDark={themeMode} /></td>
                                            <td className={`px-6 py-4 border-b ${borderThem} text-right`}><button className={`p-2 rounded-xl ${themeMode ? 'text-slate-500 hover:text-blue-400' : 'text-slate-300 hover:text-blue-600'} transition-all`}><ArrowUpRight size={18} /></button></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default DepartmentDashboardContent;